import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import styles from "../EventDashboardV2.module.scss"
import { IEvent } from "../../../components/Events/types"
import { useAppDispatch } from "../../../redux/store"
import { copyEvent } from "../../../redux/slices/events/eventsSlice"

interface EventHeaderProps {
  event: IEvent
  onAddGuests?: () => void
}

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)

const LocationIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
)

const QrCodeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="2" width="8" height="8" rx="1" />
    <rect x="14" y="2" width="8" height="8" rx="1" />
    <rect x="2" y="14" width="8" height="8" rx="1" />
    <rect x="14" y="14" width="4" height="4" />
    <line x1="22" y1="14" x2="22" y2="14.01" />
    <line x1="22" y1="20" x2="22" y2="22" />
    <line x1="18" y1="22" x2="18" y2="22.01" />
  </svg>
)

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
)

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
)

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
)

// Inline spinner for the "Duplicate as regular event" loading state. CSS
// in EventDashboardV2.module.scss applies the rotation animation.
const Spinner = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <path d="M21 12a9 9 0 1 1-6.2-8.55" strokeLinecap="round" />
  </svg>
)

const formatEventDate = (datetime: string | undefined, locale: string): string => {
  if (!datetime) return ""
  try {
    const date = new Date(datetime)
    return date.toLocaleDateString(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
  } catch {
    return datetime
  }
}

const formatCreatedDate = (dateStr: string | undefined, locale: string): string => {
  if (!dateStr) return ""
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString(locale, {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })
  } catch {
    return dateStr
  }
}

// Build the public registration URL for an open event. We show a short,
// human-friendly form (app.mazoom.sa/register/<hash>) and copy the full
// URL — matching the legacy surface so link behaviour stays consistent.
const buildRegistrationUrls = (openEventHash: string) => {
  const absolute =
    typeof window !== "undefined" && window.location?.origin
      ? `${window.location.origin}/register/${openEventHash}`
      : `https://app.mazoom.sa/register/${openEventHash}`
  const display = `app.mazoom.sa/register/${openEventHash}`
  return { absolute, display }
}

const EventHeader: React.FC<EventHeaderProps> = ({ event, onAddGuests }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { event_id } = useParams()
  const { t, i18n } = useTranslation()
  const locale = i18n.language === "ar" ? "ar-SA" : "en-US"
  const [copied, setCopied] = useState(false)

  // Duplicate flow: tracks the "Duplicate as regular event" action so the
  // footer can swap between the trigger button, a loading state, an inline
  // success (with a "View new event" link into the new dashboard-v2), or
  // an error/retry — no modal, consistent with the Copy link pattern.
  type DuplicateState = "idle" | "loading" | "success" | "error"
  const [duplicateState, setDuplicateState] = useState<DuplicateState>("idle")
  const [duplicatedEventId, setDuplicatedEventId] = useState<string | number | null>(null)

  const venue = event.venue || event.address?.address || ""
  const dateStr = formatEventDate(event.start_datetime, locale)
  const createdDate = formatCreatedDate(event.event_created_date, locale)
  const isOpenEvent = event.is_open_event === 1 && !!event.open_event_hash
  const registration = event.open_event_hash
    ? buildRegistrationUrls(event.open_event_hash)
    : null

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1800)
    return () => clearTimeout(timer)
  }, [copied])

  const handleCopyLink = async () => {
    if (!registration) return
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(registration.absolute)
      } else {
        const el = document.createElement("textarea")
        el.value = registration.absolute
        document.body.appendChild(el)
        el.select()
        document.execCommand("copy")
        document.body.removeChild(el)
      }
      setCopied(true)
    } catch {
      // Fail silently — user can still click the link.
    }
  }

  const handleDuplicate = async () => {
    if (!event.event_id || duplicateState === "loading") return
    setDuplicateState("loading")
    try {
      // Thunk resolves to `res.data` (see eventsSlice.copyEvent); the API
      // wraps the created event one level deeper under `data.event_id`.
      const payload: any = await dispatch(
        copyEvent({ event_id: event.event_id }),
      ).unwrap()
      const newId = payload?.data?.event_id
      if (!newId) throw new Error("Missing event_id in copyEvent response")
      setDuplicatedEventId(newId)
      setDuplicateState("success")
    } catch {
      setDuplicateState("error")
    }
  }

  const handleOpenDuplicate = () => {
    if (!duplicatedEventId) return
    // Route straight into the new event's V2 dashboard so the user lands
    // on the same surface they just came from, just for the copy.
    navigate(`/${duplicatedEventId}/dashboard-v2`)
  }

  return (
    <div className={styles.eventHeader}>
      <div className={styles.eventHeaderTop}>
        <div className={styles.eventTitleRow}>
          <h1 className={styles.eventTitle}>{event.name}</h1>
          {isOpenEvent && (
            <span className={styles.openBadge} aria-label={t("eventDashboard.openEventBadge")}>
              <GlobeIcon />
              {t("eventDashboard.openEventBadge")}
            </span>
          )}
        </div>
        <div className={styles.eventActions}>
          <button
            className={styles.iconBtn}
            onClick={() => navigate(`/${event_id}`)}
            title={t("eventDashboardV2.actions.delete")}
          >
            <TrashIcon />
          </button>
          <button
            className={styles.iconBtn}
            onClick={() => navigate(`/qr-design/${event_id}`)}
            title={t("eventDashboardV2.actions.qrCode")}
          >
            <QrCodeIcon />
          </button>
          <button
            className={styles.iconBtn}
            onClick={() => navigate(`/event-edit/${event.event_id}`)}
            title={t("eventDashboardV2.actions.edit")}
          >
            <EditIcon />
          </button>
          <button className={styles.addGuestsBtn} onClick={onAddGuests}>
            {t("eventDashboardV2.actions.addGuests")}
            <ChevronDownIcon />
          </button>
        </div>
      </div>

      <div className={styles.eventMeta}>
        {dateStr && (
          <span className={styles.eventMetaItem}>
            <CalendarIcon />
            {dateStr}
          </span>
        )}
        {dateStr && venue && <span className={styles.eventMetaDivider} />}
        {venue && (
          <span className={styles.eventMetaItem}>
            <LocationIcon />
            {venue}
            {event.address?.lat && (
              <>
                {" · "}
                <a
                  className={styles.eventMetaLink}
                  href={`https://maps.google.com/?q=${event.address.lat},${event.address.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("eventDashboardV2.actions.openMap")}
                </a>
              </>
            )}
          </span>
        )}
        {createdDate && (
          <>
            <span className={styles.eventMetaDivider} />
            <span className={styles.eventMetaItem}>
              {t("eventDashboardV2.labels.created")}: {createdDate}
            </span>
          </>
        )}
      </div>

      {isOpenEvent && registration && (
        // Two-zone card:
        //   Zone A (share) — heading + helper + URL pill + Copy action.
        //   Zone B (event type footer) — "Event type · Open" + Convert.
        // Splitting them stops the old single strip from making the
        // rare "Convert to regular event" action read as if it were
        // part of the everyday "copy link" flow.
        <div className={styles.registrationCard}>
          <div className={styles.registrationShare}>
            <div className={styles.registrationShareText}>
              <h3 className={styles.registrationShareHeading}>
                {t("eventDashboard.shareHeading")}
              </h3>
              <p className={styles.registrationShareHelp}>
                {t("eventDashboard.shareHelp")}
              </p>
            </div>
            <div className={styles.registrationShareRow}>
              <a
                href={registration.absolute}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.registrationUrlField}
                title={registration.absolute}
              >
                {registration.display}
              </a>
              <button
                type="button"
                onClick={handleCopyLink}
                className={
                  copied
                    ? `${styles.registrationCopyBtn} ${styles.registrationCopyBtnCopied}`
                    : styles.registrationCopyBtn
                }
                aria-live="polite"
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
                {copied
                  ? t("eventDashboard.linkCopied")
                  : t("eventDashboard.copyRegistrationLink")}
              </button>
            </div>
          </div>

          <div className={styles.registrationFooter}>
            <span className={styles.registrationFooterMeta}>
              <span className={styles.registrationFooterLabel}>
                {t("eventDashboard.eventTypeLabel")}
              </span>
              <span className={styles.registrationFooterSep} aria-hidden>
                ·
              </span>
              <span className={styles.registrationFooterValue}>
                {t("eventDashboard.eventTypeOpen")}
              </span>
            </span>
            {duplicateState === "idle" && (
              <button
                type="button"
                onClick={handleDuplicate}
                className={styles.registrationFooterAction}
              >
                {t("eventDashboard.duplicateAsRegular")}
                <ArrowRightIcon />
              </button>
            )}

            {duplicateState === "loading" && (
              <span
                className={`${styles.registrationFooterAction} ${styles.registrationFooterActionLoading}`}
                aria-live="polite"
              >
                <Spinner />
                {t("eventDashboard.duplicating")}
              </span>
            )}

            {duplicateState === "success" && (
              <span
                className={styles.registrationFooterSuccess}
                aria-live="polite"
              >
                <span className={styles.registrationFooterSuccessLabel}>
                  <CheckIcon />
                  {t("eventDashboard.duplicateSuccess")}
                </span>
                <button
                  type="button"
                  onClick={handleOpenDuplicate}
                  className={`${styles.registrationFooterAction} ${styles.registrationFooterActionPrimary}`}
                >
                  {t("eventDashboard.viewNewEvent")}
                  <ArrowRightIcon />
                </button>
              </span>
            )}

            {duplicateState === "error" && (
              <span
                className={styles.registrationFooterError}
                role="alert"
              >
                <span className={styles.registrationFooterErrorLabel}>
                  {t("eventDashboard.duplicateError")}
                </span>
                <button
                  type="button"
                  onClick={handleDuplicate}
                  className={styles.registrationFooterAction}
                >
                  {t("eventDashboard.tryAgain")}
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default EventHeader
